import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Search, Inbox, CircleDot, Clock, Archive as ArchiveIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { TicketStatusBadge, TicketPriorityBadge } from '../../components/support/StatusBadge';
import { AdminTicketDetail } from '../../components/support/AdminTicketDetail';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STAT_CARDS = [
  { key: 'total', label: 'Total Tickets', icon: Inbox, color: 'text-slate-600' },
  { key: 'new', label: 'New', icon: CircleDot, color: 'text-emerald-600' },
  { key: 'open', label: 'Open', icon: CircleDot, color: 'text-blue-600' },
  { key: 'waiting', label: 'Waiting', icon: Clock, color: 'text-amber-600' },
  { key: 'archived', label: 'Archived', icon: ArchiveIcon, color: 'text-orange-600' },
];

function TicketRow({ ticket, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-slate-50 transition-colors"
      data-testid={`admin-ticket-row-${ticket.ticket_number}`}
    >
      <div className="min-w-0 flex-1">
        <p className="font-medium text-slate-800 truncate">#{ticket.ticket_number} &mdash; {ticket.subject}</p>
        <p className="text-xs text-slate-400 mt-0.5">{ticket.customer_name} &bull; {new Date(ticket.updated_at).toLocaleString()}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {ticket.tags?.slice(0, 2).map((t) => (
          <span key={t} className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{t}</span>
        ))}
        <TicketPriorityBadge priority={ticket.priority} />
        <TicketStatusBadge status={ticket.status} />
      </div>
    </button>
  );
}

export default function AdminSupportTickets() {
  const [stats, setStats] = useState({ total: 0, new: 0, open: 0, waiting: 0, archived: 0 });
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState('');
  const [activeTicketId, setActiveTicketId] = useState(null);
  const [activeTicket, setActiveTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    const res = await axios.get(`${BACKEND_URL}/api/admin/tickets/stats`, { headers: getHeaders() });
    setStats(res.data);
  }, []);

  const fetchTags = useCallback(async () => {
    const res = await axios.get(`${BACKEND_URL}/api/admin/tickets/tags`, { headers: getHeaders() });
    setTags(res.data);
  }, []);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      if (tagFilter !== 'all') params.tag = tagFilter;
      if (search.trim()) params.search = search.trim();
      const res = await axios.get(`${BACKEND_URL}/api/admin/tickets`, { headers: getHeaders(), params });
      setTickets(res.data);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, tagFilter, search]);

  useEffect(() => { fetchStats(); fetchTags(); }, [fetchStats, fetchTags]);
  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const refreshAll = () => { fetchStats(); fetchTickets(); fetchTags(); };

  const openTicket = async (id) => {
    setActiveTicketId(id);
    const res = await axios.get(`${BACKEND_URL}/api/admin/tickets/${id}`, { headers: getHeaders() });
    setActiveTicket(res.data);
  };

  const refreshActiveTicket = async () => {
    if (!activeTicketId) return;
    const res = await axios.get(`${BACKEND_URL}/api/admin/tickets/${activeTicketId}`, { headers: getHeaders() });
    setActiveTicket(res.data);
    refreshAll();
  };

  const newTickets = tickets.filter((t) => t.status === 'new');
  const waitingTickets = tickets.filter((t) => t.status === 'waiting');

  return (
    <div className="space-y-6" data-testid="admin-support-tickets-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-slate-800">Support Tickets</h1>
      </div>

      {/* Stat boxes */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {STAT_CARDS.map((s) => (
          <button
            key={s.key}
            onClick={() => setStatusFilter(s.key === 'total' ? 'all' : s.key)}
            className={`bg-white border rounded-lg p-4 text-left hover:shadow-md transition-all ${statusFilter === s.key ? 'ring-2 ring-[#6e2ea8]/40' : ''}`}
            data-testid={`admin-stat-${s.key}`}
          >
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold text-slate-800">{stats[s.key] ?? 0}</div>
              <s.icon className={`w-4 h-4 ${s.color}`} />
            </div>
            <div className="text-xs text-slate-500 mt-1">{s.label}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-64">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search subject, customer, #ticket..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="admin-support-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36" data-testid="admin-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="waiting">Waiting</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-36" data-testid="admin-filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tagFilter} onValueChange={setTagFilter}>
          <SelectTrigger className="w-36" data-testid="admin-filter-tag"><SelectValue placeholder="Tag" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tags</SelectItem>
            {tags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Full ticket list (1 column) */}
      <Card data-testid="admin-all-tickets-list">
        <CardHeader className="py-4 bg-slate-50 border-b border-slate-100">
          <CardTitle className="text-base font-semibold">All Tickets</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center text-slate-400 text-sm">Loading...</div>
          ) : tickets.length === 0 ? (
            <div className="py-12 text-center text-slate-500 text-sm">No tickets match these filters.</div>
          ) : (
            <div className="divide-y max-h-[480px] overflow-y-auto">
              {tickets.map((t) => <TicketRow key={t.id} ticket={t} onClick={() => openTicket(t.id)} />)}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 2-column: New / Waiting */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card data-testid="admin-new-tickets-list">
          <CardHeader className="py-4 bg-emerald-50 border-b border-emerald-100">
            <CardTitle className="text-base font-semibold text-emerald-700">New ({newTickets.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {newTickets.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-sm">Nothing new.</div>
            ) : (
              <div className="divide-y max-h-72 overflow-y-auto">
                {newTickets.map((t) => <TicketRow key={t.id} ticket={t} onClick={() => openTicket(t.id)} />)}
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="admin-waiting-tickets-list">
          <CardHeader className="py-4 bg-amber-50 border-b border-amber-100">
            <CardTitle className="text-base font-semibold text-amber-700">Waiting ({waitingTickets.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {waitingTickets.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-sm">Nothing waiting.</div>
            ) : (
              <div className="divide-y max-h-72 overflow-y-auto">
                {waitingTickets.map((t) => <TicketRow key={t.id} ticket={t} onClick={() => openTicket(t.id)} />)}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AdminTicketDetail
        ticket={activeTicket}
        onClose={() => { setActiveTicketId(null); setActiveTicket(null); }}
        onUpdated={refreshActiveTicket}
      />
    </div>
  );
}
